import { type ActionResult, HttpStatus, type HttpStatusCode, type UpperSnakeCase } from '../../lib/entry.js';

export type ErrorCode<TErrorCode extends string = string> =
  | UpperSnakeCase<'UNAUTHENTICATED' | 'UNAUTHORIZED' | 'NOT_FOUND' | 'UNKNOWN' | 'INTERNAL' | 'CLIENT' | 'VALIDATION'>
  | UpperSnakeCase<TErrorCode>;

type ErrorResult<TCode extends string> = Extract<ActionResult<undefined, TCode>, { status: 'ERROR' }>;

export abstract class PoolbettingError<TCode extends string = ErrorCode> extends Error {
  protected httpStatus: HttpStatusCode = HttpStatus.InternalServerError;
  private readonly errorCode: TCode | 'UNKNOWN';

  constructor(
    message = 'Generic error',
    options?: ErrorOptions,
    code: (TCode & UpperSnakeCase<TCode>) | 'UNKNOWN' = 'UNKNOWN'
  ) {
    super(message, options);
    this.name = 'PoolbettingError';
    this.errorCode = code;
  }

  get code(): TCode | 'UNKNOWN' {
    return this.errorCode;
  }

  get status(): HttpStatusCode {
    return this.httpStatus;
  }

  static isInstance<TErrorCode extends string = ErrorCode>(value: unknown): value is PoolbettingError<TErrorCode> {
    return value instanceof PoolbettingError;
  }

  toActionResult(): ErrorResult<TCode | 'UNKNOWN'> {
    return { status: 'ERROR', error: this.message, reason: this.code };
  }
}
